<script lang="ts">
	import { format } from 'svelte-i18n';
	import type { EnergyTariff } from 'src/types/EnergyTariff';
	import { buildVerivoxRedirectUrl } from '../../api/verivoxRedirect';
	import { energyPriceApi } from '../../api/verivoxApi';
	import { openExternalLink } from 'src/helpers/utilities';
	import {
		ButtonV2,
		Text,
		// Label,
		// Indicator,
		Icon,
	} from '@Shelly/shelly-design-system';
	import MonthlyAverageCostModal from '../../modals/monthly/MonthlyAverageCostModal.svelte';
	import SavingsCalculationModal from '../../modals/savings/SavingsCalculationModal.svelte';
	import TariffRemarks from './TariffRemarks.svelte';
	import TariffContractDetails from './TariffContractDetails.svelte';
	import TariffBonuses from './TariffBonuses.svelte';
	import ProviderLogo from './ProviderLogo.svelte';
	import TariffPricing from './TariffPricing.svelte';
	import TotalCostsDisplay from './TotalCostsDisplay.svelte';
	import ProviderDetails from './ProviderDetails.svelte';
	import { createFunnel } from 'src/helpers/funnel_tracker';

	const verivoxFunnel = createFunnel('verivox_setup');

	// Helper function to format numbers with comma as decimal separator
	const formatNumber = (value: number, decimals: number = 2): string => {
		return value.toFixed(decimals).replace('.', ',');
	};

	// Calculate monthly cost from annual if not provided
	$: monthlyCost = tariff.monthlyCost || (tariff.totalCosts ? tariff.totalCosts / 12 : 0);
	$: monthlyCostFormatted = monthlyCost > 0 ? formatNumber(monthlyCost) : '0,00';
	$: pricePerKwhCents = tariff.pricePerKwh ? formatNumber(tariff.pricePerKwh * 100) : '0,00';
	$: basePriceMonthly = tariff.basePrice ? formatNumber(tariff.basePrice / 12) : '0,00';

	export let tariff: EnergyTariff;
	// export let isBest: boolean = false;
	export let variant: 'light' | 'dark' = 'light';
	export let layout: 'normal' | 'condensed' = 'normal';
	export let currency: string = '€';
	export let postalCode: string = '';
	export let locationId: string = '';
	export let totalUsage: number = 2500;
	export let subPartnerId: string = '5762';
	export let campaignId: string = '2';
	export let backgroundGradient: string | undefined;

	let showMonthlyAverageModal = false;
	let showSavingsModal = false;

	// Get actual totalUsage - use cached consumption if totalUsage is default
	$: actualTotalUsage =
		energyPriceApi.getCachedConsumption() !== null
			? energyPriceApi.getCachedConsumption()!
			: totalUsage;

	$: isClickable = layout === 'condensed' && (tariff.signupUrl || (postalCode && locationId));
	$: cardRole = (() => {
		if (layout !== 'condensed') return undefined;
		return isClickable ? 'button' : 'presentation';
	})();

	const handleButtonClick = () => {
		verivoxFunnel.incrementCounter('tariff_clicks');
		// Prioritize signupUrl from the tariff data if available
		if (tariff.signupUrl) {
			openExternalLink(tariff.signupUrl);
		} else if (postalCode && locationId) {
			// Fallback to redirect URL if signupUrl is not available
			// Use cached consumption if totalUsage is default, otherwise use provided totalUsage
			const cachedConsumption = energyPriceApi.getCachedConsumption();
			const usageToUse =
				totalUsage === 2500 && cachedConsumption !== null ? cachedConsumption : totalUsage;

			const redirectUrl = buildVerivoxRedirectUrl(
				postalCode,
				locationId,
				usageToUse,
				subPartnerId,
				campaignId,
			);
			openExternalLink(redirectUrl);
		}
	};
</script>

<svelte:head>
	<meta name="viewport" content="width=device-width, initial-scale=1.0" />
</svelte:head>

<div
	class="price-option width-full"
	class:variant-light={variant === 'light'}
	class:layout-condensed={layout === 'condensed'}
	class:layout-normal={layout === 'normal'}
	class:clickable={isClickable}
	role={cardRole}
	style={backgroundGradient && variant === 'light' ? `background: ${backgroundGradient}` : ''}
	{...isClickable ? { tabindex: 0 } : {}}
	on:click={(e) => {
		if (isClickable) {
			handleButtonClick();
		}
	}}
	on:keydown={(e) => {
		if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
			e.preventDefault();
			handleButtonClick();
		}
	}}
>
	{#if tariff.isVerivoxVerified && layout === 'normal'}
		<div class="verification-badge">
			<i class="fas fa-shield-alt verification-icon"></i>
			<Text styleType="body-extra-small" class="verification-text">
				{$format('label_verivox_verified', { default: 'Verivox verifiziert', locale: 'de' })}
			</Text>
		</div>
	{/if}
	<!-- {#if tariff.isSponsored}
		<Label
			value={$format('badge_sponsored_results', { default: 'Gesponserte Ergebnisse', locale: 'de' })}
			type="primary"
			size="s"
			noRadiusType="second"
			class="sponsored-badge"
		/>
	{:else if isBest}
		<Indicator
			title={$format('badge_best_deal', { default: 'Beste', locale: 'de' })}
			type="success"
			class="best-badge"
		/>
	{/if} -->
	<div class="option-header">
		<ProviderLogo logoUrl={tariff.logoUrl} provider={tariff.provider} {layout} {variant} />
		<div class="option-info">
			<ProviderDetails provider={tariff.provider} name={tariff.name} {layout} />
			{#if layout === 'normal'}
				{#if tariff.bonuses}
					<TariffBonuses bonuses={tariff.bonuses} {variant} />
				{/if}
				<TariffContractDetails {tariff} {variant} />
				{#if tariff.remarks}
					<TariffRemarks remarks={tariff.remarks} {variant} />
				{/if}
			{/if}
		</div>
		<div class="header-right">
			{#if layout === 'normal' && monthlyCost > 0}
				<div class="monthly-cost-container">
					<span class="monthly-cost">{monthlyCostFormatted} {currency}</span>
					<div class="cost-details">
						{#if tariff.cost}
							<div
								class="cost-label-wrapper"
								role="button"
								tabindex="0"
								on:click={() => {
									showMonthlyAverageModal = true;
								}}
								on:keydown={(e) => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										showSavingsModal = true;
									}
								}}
							>
								<Text styleType="body-extra-small" class="cost-label">
									{$format('label_average_per_month', {
										default: 'Durchschnitt pro Monat',
										locale: 'de',
									})}
								</Text>
								<Icon
									icon="fa-solid fa-circle-info"
									size="s"
									aria-label={$format('energy_price_monthly_average_info', {
										default: 'Informationen zum monatlichen Durchschnitt',
										locale: 'de',
									})}
								/>
							</div>
						{/if}
						{#if tariff.totalCosts !== undefined}
							<Text styleType="body-extra-small" class="annual-cost-small">
								{formatNumber(tariff.totalCosts)}
								{currency}
								{$format('label_annual', { default: 'jährlich', locale: 'de' })}
							</Text>
						{/if}
						{#if tariff.savings?.amount && parseFloat(tariff.savings.amount) > 0}
							{#if tariff.savings?.content?.tooltip}
								<div class="savings-wrapper mt-m">
									<div
										class="savings-amount-container"
										role="button"
										tabindex="0"
										on:click={() => {
											showSavingsModal = true;
										}}
										on:keydown={(e) => {
											if (e.key === 'Enter' || e.key === ' ') {
												e.preventDefault();
												showSavingsModal = true;
											}
										}}
									>
										<Text styleType="body" element="h3" class="savings-amount bold">
											{formatNumber(parseFloat(tariff.savings.amount))}
											{currency}
											{$format('label_saved', {
												default: 'gespart',
												locale: 'de',
											})}
										</Text>
										<Icon
											icon="fa-solid fa-circle-info"
											variant="help"
											size="s"
											aria-label={$format('energy_price_savings_info', {
												default: 'Informationen zur Ersparnisberechnung',
												locale: 'de',
											})}
										/>
									</div>
								</div>
							{:else}
								<Text styleType="body-small" class="savings-amount">
									{formatNumber(parseFloat(tariff.savings.amount))}
									{currency}
									{$format('label_saved', { default: 'saved' })}
								</Text>
							{/if}
						{/if}
					</div>
				</div>
			{:else if tariff.totalCosts !== undefined}
				<TotalCostsDisplay
					totalCosts={tariff.totalCosts}
					{currency}
					{totalUsage}
					{variant}
					{layout}
				/>
			{/if}
		</div>
	</div>
	{#if layout === 'condensed'}
		<!-- Condensed layout: hide pricing section and footer -->
	{:else}
		<TariffPricing
			{tariff}
			{pricePerKwhCents}
			{basePriceMonthly}
			pricePerKwh={tariff.pricePerKwh}
			basePrice={tariff.basePrice}
			{variant}
		/>
		{#if tariff.signupUrl || (postalCode && locationId)}
			<div class="card-footer">
				<ButtonV2 variant="primary" size="s" width="full" on:click={handleButtonClick}>
					{$format('button_explore_tariff', {
						default: 'Tarifdetails anzeigen',
						locale: 'de',
					})}
				</ButtonV2>
			</div>
		{/if}
	{/if}
</div>

<!-- Monthly Average Cost Modal -->
{#if tariff.cost}
	<MonthlyAverageCostModal
		bind:showModal={showMonthlyAverageModal}
		{tariff}
		totalUsage={actualTotalUsage}
	/>
{/if}

<!-- Savings Calculation Modal -->
{#if tariff.savings?.content?.tooltip}
	<SavingsCalculationModal bind:showModal={showSavingsModal} {tariff} />
{/if}

<style>
	.price-option {
		border-radius: 8px;
		padding: 12px;
		transition: all 0.2s ease;
		display: flex;
		flex-direction: column;
		gap: 8px;
		position: relative;
	}

	.price-option.layout-condensed {
		border-radius: 6px !important;
		padding: 6px 8px !important;
		gap: 2px !important;
	}

	.price-option.layout-condensed.clickable {
		cursor: pointer;
		transition:
			transform 0.2s ease,
			box-shadow 0.2s ease;
	}

	.price-option.layout-condensed.clickable:hover {
		transform: translateY(-1px);
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
	}

	.price-option.layout-condensed.clickable:active {
		transform: translateY(0);
	}

	.price-option.layout-condensed.clickable:focus {
		outline: 2px solid rgba(255, 255, 255, 0.5);
		outline-offset: 2px;
	}

	.price-option:hover {
		transform: translateY(-2px);
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
	}

	.price-option.variant-light {
		background: rgba(255, 255, 255, 0.15);
		backdrop-filter: blur(10px);
		border: 1px solid rgba(255, 255, 255, 0.2);
	}

	:global(.energy-price-suggestion) .price-option.variant-light,
	:global(.energy-price-tariffs-wrapper) .price-option.variant-light {
		background: rgba(255, 255, 255, 0.15);
		backdrop-filter: blur(10px);
		border: 1px solid rgba(255, 255, 255, 0.2);
	}

	.price-option.variant-light:hover {
		background: rgba(255, 255, 255, 0.2);
	}

	.price-option.variant-light.best {
		background: rgba(255, 255, 255, 0.25);
		border: 2px solid rgba(255, 255, 255, 0.4);
	}

	.price-option.variant-light .option-price {
		color: #ffffff;
	}

	.price-option.variant-dark {
		background: rgba(0, 0, 0, 0.05);
		border: 1px solid rgba(0, 0, 0, 0.1);
	}

	.price-option.variant-dark:hover {
		background: rgba(0, 0, 0, 0.08);
	}

	.price-option.variant-dark.best {
		background: rgba(16, 185, 129, 0.1);
		border: 2px solid rgba(16, 185, 129, 0.3);
		box-shadow: 0 2px 8px rgba(16, 185, 129, 0.15);
	}

	.price-option.variant-dark .option-price {
		color: var(--color-text-primary, #000);
	}

	.option-header {
		display: flex;
		align-items: flex-start;
		gap: 8px;
		margin-bottom: 4px;
		position: relative;
	}

	.price-option.layout-condensed .option-header {
		gap: 4px !important;
		margin-bottom: 0 !important;
		align-items: center !important;
	}

	.header-right {
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		gap: 4px;
		flex-shrink: 0;
	}

	.option-info {
		flex: 1;
		display: flex;
		flex-direction: column;
		gap: 1px;
		min-width: 0;
	}

	.price-option.layout-condensed .option-info {
		gap: 0px;
	}

	.verification-badge {
		position: absolute;
		top: 8px;
		left: 8px;
		z-index: 10;
		display: flex;
		align-items: center;
		gap: 4px;
		background: rgba(255, 107, 53, 0.95);
		color: #ffffff;
		padding: 4px 8px;
		border-radius: 4px;
		font-size: 10px;
		font-weight: 600;
	}

	.verification-icon {
		font-size: 12px;
	}

	.verification-text {
		color: #ffffff;
		margin: 0;
		font-size: 10px;
		line-height: 1;
	}

	.monthly-cost-container {
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		gap: 2px;
	}

	.monthly-cost {
		font-size: 28px;
		font-weight: 700;
		line-height: 1;
		color: var(--color-text-primary, #000000);
	}

	.price-option.variant-light .monthly-cost {
		color: #ffffff;
	}

	.price-option.variant-dark .monthly-cost {
		color: var(--color-text-primary, #000000);
	}

	.cost-details {
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		gap: 2px;
	}

	.cost-label-wrapper {
		cursor: pointer;
		display: flex;
		align-items: center;
		gap: 4px;
	}

	.cost-label {
		color: var(--color-text-secondary, #6b7280);
		font-size: 11px;
		margin: 0;
	}

	.monthly-average-info-icon {
		opacity: 0.7;
		transition: opacity 0.2s ease;
		flex-shrink: 0;
		transform: scale(0.85);
	}

	.monthly-average-info-icon:hover {
		opacity: 1;
	}

	.price-option.variant-light .monthly-average-info-icon {
		color: rgba(255, 255, 255, 0.8);
	}

	.price-option.variant-dark .monthly-average-info-icon {
		color: var(--color-text-secondary, #6b7280);
	}

	.popover-content {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
	}

	.popover-title {
		font-weight: 600;
		margin: 0;
	}

	.popover-description {
		margin: 0;
		line-height: 1.5;
	}

	.price-option.variant-light .cost-label {
		color: rgba(255, 255, 255, 0.8);
	}

	.annual-cost-small {
		color: var(--color-text-secondary, #6b7280);
		font-size: 10px;
		margin: 0;
	}

	.price-option.variant-light .annual-cost-small {
		color: rgba(255, 255, 255, 0.7);
	}

	.savings-wrapper {
		display: flex;
		align-items: center;
		gap: 4px;
		margin-top: 2px;
	}

	.savings-amount-container {
		cursor: pointer;
		display: flex;
		align-items: center;
		gap: 4px;
	}

	.savings-amount {
		color: #ff6b35;
		font-size: 12px;
		font-weight: 700;
		margin: 0;
	}

	.price-option.variant-light .savings-amount {
		color: rgba(255, 107, 53, 0.95);
	}

	.savings-info-icon {
		opacity: 0.7;
		transition: opacity 0.2s ease;
		flex-shrink: 0;
		transform: scale(0.85);
	}

	.savings-info-icon:hover {
		opacity: 1;
	}

	.price-option.variant-light .savings-info-icon {
		color: rgba(255, 255, 255, 0.8);
	}

	.price-option.variant-dark .savings-info-icon {
		color: var(--color-text-secondary, #6b7280);
	}

	:global(.best-badge),
	:global(.sponsored-badge) {
		position: absolute;
		top: -1px;
		left: -1px;
		z-index: 10;
	}

	.card-footer {
		margin-top: 8px;
		padding-top: 8px;
		border-top: 1px solid rgba(255, 255, 255, 0.1);
	}

	.price-option.variant-dark .card-footer {
		border-top-color: rgba(0, 0, 0, 0.1);
	}

	.option-info :global(.body-small-styles) {
		font-size: 10px;
		line-height: 1.2;
		margin: 0;
	}

	@media (max-width: 768px) {
		.price-option {
			padding: 12px;
			gap: 12px;
		}

		.price-option.layout-normal .option-header {
			flex-direction: column;
			align-items: flex-start;
			gap: 12px;
		}

		.price-option.layout-normal .header-right {
			align-items: flex-start;
			width: 100%;
		}

		.price-option.layout-normal .monthly-cost-container {
			align-items: flex-start;
			width: 100%;
		}

		.price-option.layout-normal .monthly-cost {
			font-size: 24px;
			line-height: 1.2;
		}

		.price-option.layout-normal .cost-details {
			align-items: flex-start;
			gap: 4px;
			margin-top: 4px;
		}

		.price-option.layout-normal .card-footer {
			margin-top: 12px;
			padding-top: 12px;
		}

		.option-price {
			font-size: 13px;
		}

		.price-option.layout-condensed {
			padding: 4px 6px !important;
		}

		.option-info :global(.body-small-styles) {
			font-size: 9px;
			line-height: 1.15;
		}

		.contract-item {
			font-size: 9px;
			line-height: 1.15;
		}
	}
</style>
